'use client'

import { useCallback, useEffect, useRef } from 'react'

import { useBrowserNavigation } from '@marketplace-web/browser/browser-navigation-util'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useSession } from '@marketplace-web/shared/session-data'
import { leaveFaqEntryEvent } from '@marketplace-web/help-center/help-center-faq-content-data'
import { noticesUserOpenFaq } from '@marketplace-web/help-center-support-tickets/help-center-support-tickets-data'
import useHelpCenterSessionTracking from '@marketplace-web/help-center/help-center-session-tracking-util'

type Props = {
  faqEntryId: number
  accessChannel?: string | undefined
}
const MS_PER_SECOND = 1000
const getCurrentTimeInSeconds = () => Math.floor(new Date().getTime() / MS_PER_SECOND)

const HelpCenterFaqEntryTrackingEvent = ({ faqEntryId, accessChannel }: Props) => {
  const { isWebview } = useSession()
  const { access_channel, thread_id } = useBrowserNavigation().searchParams

  const initialTime = useRef(getCurrentTimeInSeconds())
  const initialPageScrollDepth = useRef(0)
  const maxPageScrollDepth = useRef(0)

  const { track } = useTracking()
  const { helpCenterSessionId } = useHelpCenterSessionTracking()

  const getScrollDepth = (scrollOffset = 0) => {
    const documentHeight = document.body.scrollHeight
    const clientHeight = document.documentElement.clientHeight + scrollOffset

    if (clientHeight <= 0) {
      return 0
    }

    return Math.floor((clientHeight / documentHeight) * 100)
  }

  const handleBeforeUnload = useCallback(() => {
    if (isWebview) return
    if (Array.isArray(access_channel)) return

    const totalViewTime = getCurrentTimeInSeconds() - initialTime.current

    if (!helpCenterSessionId) return

    track(
      leaveFaqEntryEvent({
        faqEntryId,
        faqAccessChannel: access_channel || accessChannel || null,
        initialPageScrollDepth: initialPageScrollDepth.current,
        maxPageScrollDepth: maxPageScrollDepth.current,
        totalViewTime,
        helpCenterSessionId,
      }),
    )
  }, [isWebview, access_channel, track, faqEntryId, accessChannel, helpCenterSessionId])

  const handleScrollDepthUpdate = useCallback(() => {
    const visibleScrollDepth = getScrollDepth()
    const currentScrollDepth = getScrollDepth(window.pageYOffset)

    initialPageScrollDepth.current = visibleScrollDepth

    if (currentScrollDepth > maxPageScrollDepth.current) {
      maxPageScrollDepth.current = currentScrollDepth
    }
  }, [])

  useEffect(() => {
    if (isWebview) return

    if (typeof thread_id !== 'string') return

    track(noticesUserOpenFaq({ threadId: thread_id }))
  }, [isWebview, thread_id, track])

  useEffect(() => {
    const resizeObserver = new ResizeObserver(handleScrollDepthUpdate)
    const element = document.body

    resizeObserver.observe(element)

    return () => {
      resizeObserver.unobserve(element)
    }
  }, [handleScrollDepthUpdate])

  useEffect(() => {
    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [handleBeforeUnload])

  useEffect(() => {
    window.addEventListener('scroll', handleScrollDepthUpdate)

    return () => {
      window.removeEventListener('scroll', handleScrollDepthUpdate)
    }
  }, [handleScrollDepthUpdate])

  return null
}

export default HelpCenterFaqEntryTrackingEvent
